import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { images } from "../../content/siteConfig";

export const Hero = () => {
  const { t, path } = useLang();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });
  const imgX = useTransform(sx, [-1, 1], [14, -14]);
  const imgY = useTransform(sy, [-1, 1], [10, -10]);
  const glowX = useTransform(sx, [-1, 1], [-24, 24]);

  const onMove = (e) => {
    if (reduce) return;
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  const fade = (delay) => ({
    initial: reduce ? { opacity: 1 } : { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen bg-[#061A26] flex items-center overflow-hidden"
      data-testid="home-hero"
    >
      <motion.div
        style={reduce ? {} : { x: imgX, y: imgY }}
        className="absolute inset-y-0 right-0 w-full md:w-[62%]"
        aria-hidden="true"
      >
        <img
          src={images.hero}
          alt=""
          className="w-full h-full object-cover opacity-70 md:opacity-90"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#061A26] via-[#061A26]/75 md:via-[#061A26]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#061A26] via-transparent to-[#061A26]/60" />
      </motion.div>

      <motion.div
        style={reduce ? {} : { x: glowX }}
        className="absolute -bottom-40 left-1/4 w-[600px] h-[300px] rounded-full bg-[#19C7C1]/8 blur-[120px]"
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10 w-full pt-32 pb-24">
        <div className="max-w-2xl">
          <motion.p {...fade(0.1)} className="eyebrow" data-testid="hero-eyebrow">
            {t.hero.eyebrow}
          </motion.p>
          <motion.h1
            {...fade(0.25)}
            className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.08]"
            data-testid="hero-headline"
          >
            {t.hero.headline1}
            <br />
            <span className="font-semibold">{t.hero.headline2}</span>
          </motion.h1>
          <motion.p {...fade(0.4)} className="mt-7 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl" data-testid="hero-copy">
            {t.hero.copy}
          </motion.p>
          <motion.div {...fade(0.55)} className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to={path("/odf-technology")} className="btn-primary" data-testid="hero-cta-technology">
              {t.hero.cta1}
              <ArrowRight size={16} />
            </Link>
            <Link to={path("/partnerships")} className="btn-outline" data-testid="hero-cta-partner">
              {t.hero.cta2}
            </Link>
          </motion.div>
          <motion.p {...fade(0.7)} className="mt-12 text-xs tracking-[0.2em] uppercase text-slate-500" data-testid="hero-trust-line">
            {t.hero.trust}
          </motion.p>
        </div>
      </div>

      <motion.div
        {...fade(1)}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-slate-500"
        aria-hidden="true"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        <ChevronDown size={16} className="animate-bounce-slow text-[#19C7C1]" />
      </motion.div>
    </section>
  );
};
